import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import useSEOMeta from '@/hooks/useSEOMeta';
import { articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

export default function Article() {
  const { articleSlug } = useParams<{ articleSlug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === articleSlug),
    [articleSlug]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  useSEOMeta({
    title: article ? `${article.title} - 三上悠亚粉丝站` : '文章未找到 - 三上悠亚粉丝站',
    description: article ? article.summary : '您要查看的文章不存在或已被移除',
    keywords: article ? article.tags.join(',') + ',三上悠亚,三上悠亚粉丝站' : '三上悠亚,三上悠亚粉丝站',
    canonicalUrl: article ? `https://cdrmjj.cn/article/${article.slug}` : 'https://cdrmjj.cn/',
    ogType: article ? 'article' : 'website',
    ogImage: article ? article.coverImage : undefined,
  });

  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.coverImage,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Organization',
      name: '三上悠亚粉丝站',
    },
    publisher: {
      '@type': 'Organization',
      name: '三上悠亚粉丝站',
    },
    inLanguage: 'zh-CN',
  } : null;

  const categoryName = article ? (categoryMap[article.category]?.name || article.category) : '';

  return (
    <>
      {articleSchema && <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>}
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {!article ? (
          <main className="pt-24 pb-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-file-unknow-line text-2xl"></i>
              </div>
              <h1 className="mt-4 text-xl font-heading font-bold text-foreground-900">文章未找到</h1>
              <p className="mt-2 text-sm text-foreground-500">您要查看的文章不存在或已被移除</p>
              <Link
                to="/"
                className="mt-6 inline-block px-6 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-full text-sm font-label font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </main>
        ) : (
          <main className="pt-24 pb-16">
            <article className="max-w-4xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />

              <header className="mt-8">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="px-3 py-1 text-xs font-label rounded-full bg-accent-100 text-accent-700">
                    {categoryName}
                  </span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-xs font-label rounded-full bg-background-100 text-foreground-500">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="mt-5 text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                  {article.title}
                </h1>
                <div className="mt-4 flex items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.readCount.toLocaleString()} 次阅读
                  </span>
                </div>
              </header>

              <div className="mt-8 w-full aspect-[16/9] rounded-xl overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 三上悠亚粉丝站`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="mt-10 prose prose-lg max-w-none text-foreground-800 leading-relaxed
                  [&_h3]:text-xl [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-4
                  [&_p]:mb-5 [&_p]:leading-relaxed
                  [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:space-y-1
                  [&_li]:text-foreground-700 [&_li]:leading-relaxed
                  [&_strong]:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-xs font-label rounded-full bg-background-100 text-foreground-600">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((rel) => (
                    <ArticleCard key={rel.slug} article={rel} />
                  ))}
                </div>
              </section>
            )}
          </main>
        )}

        <Footer />
      </div>
    </>
  );
}